/**
 * Builds an HTML document from the current page's report root (data-report-root),
 * converts canvases to images, captures Leaflet maps as PNGs, and opens the print dialog so the user can Save as PDF.
 * Does not require displaying the HTML (opens a print dialog directly).
 */

import { toPng } from 'html-to-image';
import { NARRATIVE_METHODOLOGY_TAB_LABEL } from '../constants/narrativeTabLabels';
import { generateScenarioReport, type ScenarioReportInput, type ScenarioReportOutput } from './scenarioReportGenerator';

export type ReportPageId = 'transportation' | 'buildings' | 'people';
export type ReportExportMode = 'auto' | 'structured' | 'snapshot';

declare global {
  interface Window {
    __CLIMATEPLANS_SCENARIO_EXPORT_CONTEXT__?: ScenarioReportInput;
  }
}

const REPORT_PAGE_LABELS: Record<ReportPageId, string> = {
  transportation: 'Transportation',
  buildings: 'Energy & Buildings',
  people: 'People & Demographics'
};

const CONTENT_SECTION_LABELS = ['Overview', 'Relevance', 'Equity', NARRATIVE_METHODOLOGY_TAB_LABEL];

/**
 * Remove edit UI from clone so PDF/Word export does not show "Edit Title", "Edit Overview", etc.
 * Call when the user is Super Admin or Local Government Admin (they see edit controls on the page).
 */
function stripEditUiFromClone(clone: HTMLElement): void {
  clone.querySelectorAll('.editable-section-controls').forEach((el) => el.remove());
  clone.querySelectorAll('.editable-section-editor').forEach((el) => el.remove());
}

/**
 * Prepare cloned DOM for narrative-style report: show all Overview/Relevance/Equity/Methodology
 * sections and add a heading before each (never "Section"); remove duplicate inner headings.
 */
function prepareCloneForNarrative(clone: HTMLElement): void {
  clone.querySelectorAll('.content-section').forEach((section, i) => {
    section.classList.remove('hidden');
    const label = CONTENT_SECTION_LABELS[i];
    if (!label) return; // never add "Section" or empty scaffolding
    const heading = document.createElement('div');
    heading.className = 'report-section-heading';
    heading.textContent = label;
    section.insertBefore(heading, section.firstChild);
    // Remove duplicate inner heading if it matches this label (show label only once)
    const first = section.querySelector('h1, h2, h3, h4, h5, h6, [class*="title"]');
    if (first && typeof first.textContent === 'string') {
      const t = first.textContent.trim();
      if (
        t === label ||
        t === 'Overview' ||
        t === 'Relevance' ||
        t === 'Equity' ||
        t === 'Methodology' ||
        t === NARRATIVE_METHODOLOGY_TAB_LABEL
      ) {
        first.remove();
      }
    }
  });
}

/**
 * Replace every canvas in clonedRoot with an img using the corresponding canvas from originalRoot.
 */
function replaceCanvasesWithImages(originalRoot: Element, clonedRoot: Element): void {
  const origCanvases = originalRoot.querySelectorAll('canvas');
  const cloneCanvases = clonedRoot.querySelectorAll('canvas');
  if (origCanvases.length !== cloneCanvases.length) return;
  origCanvases.forEach((orig, i) => {
    const clone = cloneCanvases[i];
    if (!clone || !(orig instanceof HTMLCanvasElement)) return;
    try {
      const dataUrl = orig.toDataURL('image/png');
      const img = document.createElement('img');
      img.src = dataUrl;
      img.alt = 'Chart';
      img.style.maxWidth = '100%';
      img.style.height = 'auto';
      clone.parentNode?.replaceChild(img, clone);
    } catch {
      // leave canvas as-is if conversion fails
    }
  });
}

/**
 * Capture Leaflet/map containers from originalRoot as PNGs and replace corresponding
 * containers in clonedRoot with a single img so the report shows the map as a static figure.
 */
async function replaceMapsWithImages(originalRoot: Element, clonedRoot: Element): Promise<void> {
  const origSections = originalRoot.querySelectorAll('.chart-section');
  const cloneSections = clonedRoot.querySelectorAll('.chart-section');
  if (origSections.length !== cloneSections.length) return;

  for (let i = 0; i < origSections.length; i++) {
    const origSection = origSections[i];
    const mapEl =
      origSection.querySelector('.chart-container .leaflet-container') ||
      origSection.querySelector('.chart-container .map-container');
    if (!mapEl || !(mapEl instanceof HTMLElement)) continue;

    let dataUrl: string;
    try {
      dataUrl = await toPng(mapEl, {
        cacheBust: true,
        pixelRatio: 1,
        backgroundColor: '#e8e8e8',
        imagePlaceholder: 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300"><rect fill="#e8e8e8" width="400" height="300"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="#666" font-family="sans-serif" font-size="14">Map</text></svg>')
      });
    } catch {
      continue;
    }

    const cloneSection = cloneSections[i];
    const cloneContainer = cloneSection?.querySelector('.chart-container');
    if (!cloneContainer) continue;

    const img = document.createElement('img');
    img.src = dataUrl;
    img.alt = 'Map';
    img.style.maxWidth = '100%';
    img.style.height = 'auto';
    img.className = 'report-map-image';
    cloneContainer.innerHTML = '';
    cloneContainer.appendChild(img);
  }
}

/**
 * Full HTML document for report (PDF or Word). Uses stable .report layout and REPORT_STYLES only.
 */
function buildReportDocument(reportMainHtml: string, pageLabel: string, location: string): string {
  return `<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <meta name="ProgId" content="Word.Document" />
  <title>${escapeHtml(pageLabel)} – ${escapeHtml(location)}</title>
  <style>${REPORT_STYLES}</style>
</head>
<body>
${reportMainHtml}
</body>
</html>`;
}

function buildStructuredScenarioReportDocument(report: ScenarioReportOutput, location: string): string {
  const rows = (items: Array<{ label: string; value: string }>) =>
    items.map((i) => `<div class="report__key-metric"><span class="report__key-metric-label">${escapeHtml(i.label)}:</span> <span class="report__key-metric-value">${escapeHtml(i.value)}</span></div>`).join('');

  const sectorItems = report.sectorBreakdown
    .map((s) => `<li>${escapeHtml(s.sector)}: ${Math.round(s.reduction).toLocaleString()} tCO2e, ${s.cost.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })}</li>`)
    .join('');
  const policyItems = report.policyBreakdown
    .map((p) => `<li>${escapeHtml(p.policy)}: ${Math.round(p.reduction).toLocaleString()} tCO2e</li>`)
    .join('');

  const html = `
    <main class="report">
      <header class="report__header">
        <h1>Policy Scenario Report - ${escapeHtml(location)}</h1>
      </header>
      <section class="report__section">
        <h2>Executive Summary</h2>
        <p>${escapeHtml(report.executiveSummary)}</p>
      </section>
      <section class="report__section">
        <h2>Key Metrics</h2>
        <div class="report__key-metrics">
          ${rows([
            { label: 'NPV', value: report.keyMetrics.npv.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }) },
            { label: 'Cost per ton', value: report.keyMetrics.costPerTon == null ? 'N/A' : `${report.keyMetrics.costPerTon.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })} / tCO2e` },
            { label: 'Payback year', value: report.keyMetrics.paybackYear == null ? 'No payback by 2045' : String(report.keyMetrics.paybackYear) },
            { label: 'Total reduction', value: `${Math.round(report.keyMetrics.totalReduction).toLocaleString()} tCO2e` },
            { label: 'Upfront cost', value: report.keyMetrics.upfrontCost.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }) },
          ])}
        </div>
      </section>
      <section class="report__section">
        <h2>State vs Local</h2>
        <div class="report__key-metrics">
          ${rows([
            { label: 'State reduction', value: `${Math.round(report.stateVsLocal.stateReduction).toLocaleString()} tCO2e` },
            { label: 'Local reduction', value: `${Math.round(report.stateVsLocal.localReduction).toLocaleString()} tCO2e` },
            { label: 'State cost', value: report.stateVsLocal.stateCost.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }) },
            { label: 'Local cost', value: report.stateVsLocal.localCost.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }) },
          ])}
        </div>
      </section>
      <section class="report__section">
        <h2>Sector Breakdown</h2>
        <ul>${sectorItems}</ul>
      </section>
      <section class="report__section">
        <h2>Policy Breakdown</h2>
        <ul>${policyItems}</ul>
      </section>
      <section class="report__section">
        <h2>Narrative</h2>
        <p>${escapeHtml(report.narrative.summary)}</p>
        <p>${escapeHtml(report.narrative.drivers)}</p>
        <p>${escapeHtml(report.narrative.recommendations)}</p>
      </section>
      <section class="report__section">
        <h2>Assumptions</h2>
        <div class="report__key-metrics">
          ${rows([
            { label: 'Transport BAU', value: report.assumptions.transportBAU },
            { label: 'Buildings BAU', value: report.assumptions.buildingsBAU },
            { label: 'Electricity', value: report.assumptions.electricity },
            { label: 'Waste', value: report.assumptions.waste },
          ])}
        </div>
      </section>
    </main>
  `;

  return buildReportDocument(html, 'Policy Scenario Report', location);
}

function getStructuredScenarioReportDocument(location: string): string | null {
  const ctx = typeof window !== 'undefined' ? window.__CLIMATEPLANS_SCENARIO_EXPORT_CONTEXT__ : undefined;
  if (!ctx) return null;
  try {
    const report = generateScenarioReport(ctx);
    return buildStructuredScenarioReportDocument(report, location);
  } catch (e) {
    return null;
  }
}

function escapeHtml(s: string): string {
  const div = document.createElement('div');
  div.textContent = s;
  return div.innerHTML;
}

/** Block labels in order; no "Section" - only these four. */
const REPORT_BLOCK_LABELS = ['Overview', 'Relevance', 'Equity', NARRATIVE_METHODOLOGY_TAB_LABEL];

/**
 * Extract key metrics HTML from a chart section for the report (PDF/Word).
 * Preserves value color (e.g. green for negative % change, red for positive) when present.
 */
function buildKeyMetricsHtml(section: Element): string {
  const container = section.querySelector('.key-metrics-after-title .key-metrics-container') || section.querySelector('.key-metrics-after-title');
  if (!container) return '';

  const items = container.querySelectorAll('.key-metric-item');
  if (items.length === 0) return '';

  const lines: string[] = [];
  items.forEach((item) => {
    const valueEl = item.querySelector('.key-metric-value');
    const labelEl = item.querySelector('.key-metric-label');
    const valueText = valueEl?.textContent?.trim() ?? '';
    const labelText = labelEl?.textContent?.trim() ?? '';
    if (!valueText && !labelText) return;
    const color = valueEl instanceof HTMLElement ? (valueEl.style?.color || valueEl.getAttribute('style')?.match(/color:\s*([^;]+)/)?.[1]?.trim()) : undefined;
    const valueSpan = color ? `<span class="report__key-metric-value" style="color:${color}">${escapeHtml(valueText)}</span>` : `<span class="report__key-metric-value">${escapeHtml(valueText)}</span>`;
    lines.push(`<div class="report__key-metric"><span class="report__key-metric-label">${escapeHtml(labelText)}:</span> ${valueSpan}</div>`);
  });

  if (lines.length === 0) return '';
  return `<div class="report__key-metrics">${lines.join('')}</div>`;
}

/**
 * Build stable report HTML (main.report) from cloned page root.
 * Single header, then for each chart section: h2, key metrics (if any), one static figure img, four narrative blocks (h3 + body).
 * No interactive UI, no duplicate headings, no "Section".
 */
function buildReportHtmlFromClone(clone: HTMLElement, pageTitle: string, location: string): string {
  const parts: string[] = [];

  parts.push(`<header class="report__header"><h1>${escapeHtml(pageTitle)} – ${escapeHtml(location)}</h1></header>`);

  const chartSections = clone.querySelectorAll('.chart-section');
  chartSections.forEach((section) => {
    const titleEl = section.querySelector('.chart-title-section h3');
    const chartTitle = titleEl?.textContent?.trim() ?? 'Chart';

    const keyMetricsHtml = buildKeyMetricsHtml(section);

    const container = section.querySelector('.chart-container');
    const img = container?.querySelector('img');
    const imgSrc = img?.getAttribute('src') ?? '';
    const figureHtml = imgSrc ? `<img class="report__figure" src="${imgSrc.replace(/"/g, '&quot;')}" alt="${escapeHtml(chartTitle)}" />` : '';

    const contentSections = section.querySelectorAll('.content-section');
    const blocks: string[] = [];
    contentSections.forEach((contentSection, idx) => {
      const label = REPORT_BLOCK_LABELS[idx];
      if (!label) return;
      const headingEl = contentSection.querySelector('.report-section-heading');
      const bodyEl = document.createElement('div');
      bodyEl.className = 'report__block-body';
      contentSection.childNodes.forEach((node) => {
        if (node === headingEl) return;
        if (node.nodeType === Node.ELEMENT_NODE) bodyEl.appendChild((node as Element).cloneNode(true));
        else if (node.nodeType === Node.TEXT_NODE && (node.textContent?.trim() ?? '')) bodyEl.appendChild(node.cloneNode(true));
      });
      const bodyHtml = bodyEl.innerHTML.trim();
      blocks.push(`<div class="report__block"><h3>${escapeHtml(label)}</h3>${bodyHtml ? `<div class="report__block-body">${bodyHtml}</div>` : ''}</div>`);
    });

    parts.push(`<section class="report__section"><h2>${escapeHtml(chartTitle)}</h2>${keyMetricsHtml}${figureHtml}${blocks.join('')}</section>`);
  });

  return `<main class="report">${parts.join('')}</main>`;
}

/* Print styles scoped to .report only (Word narrative format) */
const REPORT_STYLES = `
  * { box-sizing: border-box; }
  @page { size: Letter; margin: 1in; }
  body {
    font-family: Calibri, "Segoe UI", Arial, sans-serif;
    font-size: 11pt;
    line-height: 1.4;
    color: #000;
    padding: 0 !important;
    margin: 0 !important;
    background: #fff !important;
  }
  .report {
    max-width: 100%;
    margin: 0;
    padding: 0;
  }
  .report__header {
    margin-bottom: 12pt;
    padding-bottom: 12pt;
    border-bottom: 1px solid #000;
  }
  .report__header h1 {
    font-size: 18pt;
    font-weight: bold;
    margin: 0;
  }
  .report__section {
    display: block;
    clear: both;
    overflow: hidden;
    margin-bottom: 24pt;
    break-inside: avoid;
    page-break-inside: avoid;
  }
  .report__section h2 {
    font-size: 14pt;
    font-weight: bold;
    margin-top: 18pt;
    margin-bottom: 8pt;
    break-after: avoid;
    page-break-after: avoid;
  }
  .report__key-metrics {
    display: block;
    margin: 8pt 0 12pt 0;
    padding: 8pt 0;
    border-bottom: 1px solid #ddd;
  }
  .report__key-metric {
    font-size: 11pt;
    margin: 0 0 4pt 0;
    line-height: 1.4;
  }
  .report__key-metric-label {
    font-weight: bold;
    margin-right: 0.25em;
  }
  .report__key-metric-value {
    font-weight: normal;
  }
  .report__figure {
    max-width: 100% !important;
    height: auto !important;
    display: block !important;
    margin: 0 auto 10pt auto !important;
  }
  .report__block {
    margin-top: 12pt;
    margin-bottom: 12pt;
    break-inside: avoid;
    page-break-inside: avoid;
  }
  .report__block h3 {
    font-size: 11pt;
    font-weight: bold;
    margin-top: 10pt;
    margin-bottom: 4pt;
    break-after: avoid;
    page-break-after: avoid;
  }
  .report__block-body,
  .report__block-body p {
    margin: 0 0 8pt 0;
    font-size: 11pt;
    display: block;
    word-wrap: break-word;
    overflow-wrap: anywhere;
  }
  .report__block-body ul {
    margin: 0 0 8pt 0;
    padding-left: 24pt;
  }
  .report__block-body li {
    margin: 0 0 4pt 0;
  }
  .report__block-body h5 {
    font-size: 11pt;
    font-weight: bold;
    margin: 8pt 0 4pt 0;
  }
  .report a[href] { text-decoration: underline; }
  @media print {
    body { padding: 0 !important; margin: 0 !important; background: #fff !important; }
    .report__section, .report__block { break-inside: avoid; page-break-inside: avoid; }
    h1, h2, h3 { break-after: avoid; page-break-after: avoid; }
  }
`;

/**
 * Build the full report HTML document (clone, canvases → img, maps → img, narrative prep, strip edit UI).
 * Returns null if the page root is not in the DOM.
 */
async function buildReportDocumentAsync(
  pageId: ReportPageId,
  location: string,
  stripEditUi?: boolean
): Promise<string | null> {
  const root = document.querySelector(`[data-report-root="${pageId}"]`);
  if (!root || !(root instanceof HTMLElement)) return null;

  const clone = root.cloneNode(true) as HTMLElement;
  replaceCanvasesWithImages(root, clone);
  await replaceMapsWithImages(root, clone);
  prepareCloneForNarrative(clone);
  if (stripEditUi) stripEditUiFromClone(clone);
  clone.querySelectorAll('script').forEach((s) => s.remove());
  clone.querySelectorAll('style').forEach((s) => s.remove());

  const pageLabel = REPORT_PAGE_LABELS[pageId];
  const reportMainHtml = buildReportHtmlFromClone(clone, pageLabel, location);
  return buildReportDocument(reportMainHtml, pageLabel, location);
}

/**
 * Opens the page report as a printable document. User can choose "Save as PDF" in the print dialog.
 * Uses a hidden iframe (no popup) so it is not blocked by the browser.
 * Only works when the given page is currently mounted (its data-report-root is in the DOM).
 */
export async function printPageReport(
  pageId: ReportPageId,
  location: string,
  stripEditUi?: boolean,
  options?: { mode?: ReportExportMode }
): Promise<boolean> {
  const mode = options?.mode ?? 'auto';
  const structuredDoc = mode === 'snapshot' ? null : getStructuredScenarioReportDocument(location);
  const doc =
    mode === 'structured'
      ? structuredDoc
      : structuredDoc ?? (await buildReportDocumentAsync(pageId, location, stripEditUi));
  if (!doc) {
    return false;
  }

  const pageLabel = REPORT_PAGE_LABELS[pageId];
  const iframe = document.createElement('iframe');
  iframe.setAttribute('title', `${pageLabel} report for print`);
  Object.assign(iframe.style, {
    position: 'fixed',
    left: '-9999px',
    top: '0',
    width: '900px',
    height: '700px',
    border: 'none',
    visibility: 'hidden'
  });
  document.body.appendChild(iframe);

  const iframeDoc = iframe.contentDocument ?? iframe.contentWindow?.document;
  if (!iframeDoc) {
    document.body.removeChild(iframe);
    return false;
  }

  iframeDoc.open();
  iframeDoc.write(doc);
  iframeDoc.close();

  const triggerPrint = () => {
    try {
      iframe.contentWindow?.print();
    } finally {
      setTimeout(() => {
        if (iframe.parentNode) document.body.removeChild(iframe);
      }, 1000);
    }
  };

  setTimeout(triggerPrint, 400);
  return true;
}

/**
 * Builds the full report HTML document (same as used for PDF). Use for Word download or preview.
 * Returns null if the page root is not in the DOM (e.g. not on that page).
 */
export async function getReportDocumentHtml(
  pageId: ReportPageId,
  location: string,
  stripEditUi?: boolean
): Promise<string | null> {
  return buildReportDocumentAsync(pageId, location, stripEditUi);
}

/**
 * Downloads the current page report as a Word-compatible document (.doc).
 * Word can open HTML saved as .doc; the report uses Word-friendly markup and styles.
 */
export async function downloadReportAsWord(
  pageId: ReportPageId,
  location: string,
  stripEditUi?: boolean,
  options?: { mode?: ReportExportMode }
): Promise<boolean> {
  const mode = options?.mode ?? 'auto';
  const structuredDoc = mode === 'snapshot' ? null : getStructuredScenarioReportDocument(location);
  const html =
    mode === 'structured'
      ? structuredDoc
      : structuredDoc ?? (await getReportDocumentHtml(pageId, location, stripEditUi));
  if (!html) return false;

  const blob = new Blob(['\ufeff' + html], { type: 'application/msword' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const safeLocation = location.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_-]/g, '');
  const safeTitle = REPORT_PAGE_LABELS[pageId].replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_-]/g, '');
  a.download = `${safeLocation}_${safeTitle}_Report.doc`;
  a.click();
  URL.revokeObjectURL(url);
  return true;
}

export { REPORT_PAGE_LABELS };
